/**
 * Il Magazzino: `/api/v1/magazzino`, non `/api/v1/m1/...`.
 *
 * La rotta sta fuori dal router di M1 perché quello è incluso con
 * `require_modulo("M1")`, e la giacenza la leggono quattro moduli. Lato
 * permessi non cambia nulla oggi — la sezione eredita da `modulo.M1` — ma
 * domani la stessa pagina si concede a chi non ha Commesse senza spostare
 * codice. Vedi il docstring di `core/api/v1/magazzino.py`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  MagImpostazioni,
  MagPallet,
  MagScadenze,
  MagazzinoResponse,
} from "@/types/api";

export const FILTRI_MAGAZZINO = [
  "a_magazzino",
  "non_ritirati",
  "liberi",
  "fermi",
  "fine_vita",
  "mancano",
  "senza_piano",
  "esclusi",
  "tutti",
] as const;

export type FiltroMagazzino = (typeof FILTRI_MAGAZZINO)[number];

export interface OpzioniMagazzino {
  filtro: FiltroMagazzino;
  cerca?: string;
  offset?: number;
  limit?: number;
}

/**
 * ⚠️ **Una chiamata sola per la pagina intera** — riquadri, tabella e totali.
 *
 * Non è un'ottimizzazione: è la proprietà che impedisce a un riquadro di
 * promettere 250 e alla lista di aprirne 315. Finché erano due endpoint
 * (`articoli-critici` + `stock-eccedente`) i numeri potevano venire da due
 * fotografie diverse, e lo facevano.
 */
export function useMagazzino(opts: OpzioniMagazzino) {
  const params = new URLSearchParams();
  params.set("filtro", opts.filtro);
  if (opts.cerca?.trim()) params.set("cerca", opts.cerca.trim());
  params.set("offset", String(opts.offset ?? 0));
  params.set("limit", String(opts.limit ?? 25));
  const qs = params.toString();
  return useQuery({
    queryKey: ["magazzino", "articoli", qs],
    queryFn: () => apiFetch<MagazzinoResponse>(`/api/v1/magazzino/articoli?${qs}`),
    staleTime: 30_000,
    // La fotografia precedente resta a schermo mentre arriva la nuova: cambiando
    // filtro o scrivendo nella ricerca, uno skeleton a ogni tasto farebbe
    // lampeggiare la pagina invece di aggiornarla.
    placeholderData: (prec) => prec,
  });
}

/** I pallet di un articolo, dal più fermo, con la loro destinazione. */
export function useMagazzinoPallet(codice: string | null) {
  return useQuery({
    queryKey: ["magazzino", "pallet", codice],
    enabled: !!codice,
    queryFn: () =>
      apiFetch<MagPallet[]>(
        `/api/v1/magazzino/articoli/${encodeURIComponent(codice ?? "")}/pallet`,
      ),
  });
}

/** Le soglie di default delle fasce di scadenza (Enrico, 12-10-2026). */
export const SOGLIE_SCADENZA_DEFAULT = [30, 60, 90, 120];

/** Pallet scaduti e in scadenza per fascia: la stessa fotografia del PDF. */
export function useScadenzeMagazzino(soglie: number[], enabled = true) {
  const fasce = soglie.join(",");
  return useQuery({
    queryKey: ["magazzino", "scadenze", fasce],
    enabled,
    queryFn: () =>
      apiFetch<MagScadenze>(`/api/v1/magazzino/scadenze?fasce=${encodeURIComponent(fasce)}`),
    staleTime: 30_000,
    placeholderData: (prec) => prec,
  });
}

export function useMagazzinoImpostazioni() {
  return useQuery({
    queryKey: ["magazzino", "impostazioni"],
    queryFn: () => apiFetch<MagImpostazioni>("/api/v1/magazzino/impostazioni"),
  });
}

/** Cambiare la shelf life muove le date di tutta la piattaforma: si invalida
 *  tutto ciò che la legge, magazzino e documenti compresi. */
function invalidaTutto(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["magazzino"] });
  qc.invalidateQueries({ queryKey: ["m3"] });
}

export function useSalvaImpostazioniMagazzino() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { shelf_life_mesi_default?: number; soglia_fine_vita_pct?: number }) =>
      apiFetch<MagImpostazioni>("/api/v1/magazzino/impostazioni", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaTutto(qc),
  });
}

export function useShelfLifeArticolo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codice, mesi }: { codice: string; mesi: number | null }) =>
      apiFetch(`/api/v1/magazzino/articoli/${encodeURIComponent(codice)}/shelf-life`, {
        method: "PATCH",
        body: JSON.stringify({ shelf_life_mesi: mesi }),
      }),
    onSuccess: () => invalidaTutto(qc),
  });
}

export function useShelfLifeBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codici, mesi }: { codici: string[]; mesi: number | null }) =>
      apiFetch<{ aggiornati: number; n_override: number }>("/api/v1/magazzino/shelf-life/bulk", {
        method: "POST",
        body: JSON.stringify({ codici, shelf_life_mesi: mesi }),
      }),
    onSuccess: () => invalidaTutto(qc),
  });
}
